import React, { type HTMLProps, type ReactNode } from 'react';
import { IconCardHotel } from '@lmn/lastminute-react-icons/lastminute-icons-linear.js';
import styled, { css, useTheme } from 'styled-components';
import { useTranslation } from '@/app/components/translations/Translation';
import { Card } from '@lmn/design-system';

const StyledToastIcon = styled(IconCardHotel)`
  color: ${({ theme }) => theme.kallisto.color.icon.static.iconSuccess};
  min-height: 52px;
  min-width: 52px;
`;

export function PayAtHotelBanner(props: React.HTMLAttributes<HTMLElement>) {
  const { ...rest } = props;
  const { t, translateWithElementPlaceholder } = useTranslation();

  return (
    <div
      {...rest}
      css={css`
        background-color: ${({ theme }) => theme.kallisto.color.background.static.backgroundLayer02};
        border-radius: 8px;
      `}
    >
      <Message type={'success'} key={'pay-at-hotel-message'}>
        <div
          css={css`
            display: flex;
            padding: 16px;
            gap: 16px;
          `}
        >
          <StyledToastIcon />
          <div
            css={css`
              display: flex;
              flex-direction: column;
            `}
          >
            <span
              css={css`
                ${({ theme }) => theme.kallisto.mixins.font('Title4')};
              `}
            >
              {t('vacation.select.details.paymentbox.title')}
            </span>
            <span>
              {translateWithElementPlaceholder(
                'vacation.select.details.paymentbox.payathotel',
                'paymentType',
                <strong key="pay-at-hotel">&quot;{t('vacation.select.details.payathotel')}&quot;</strong>
              )}
            </span>
          </div>
        </div>
      </Message>
    </div>
  );
}

type MessageProps = {
  children: ReactNode;
  /** delay to hide the toast in milliseconds. `-1` will disable the timeout. */
  timeout?: number;
  type?: 'success' | 'warning' | 'danger';
} & HTMLProps<HTMLDivElement>;

function Message(props: MessageProps) {
  const { children, type = 'success', ...rest } = props;
  const theme = useTheme();
  const color = theme.kallisto.color.common.semantic[type === 'danger' ? 'error' : type][400];

  return (
    <Card
      isClickable={false}
      isOutlined={false}
      radius="2px"
      elevation="medium"
      padding={0}
      {...rest}
      css={css`
        background:
          linear-gradient(${color}40, ${color}40),
          linear-gradient(
            ${theme.kallisto.color.background.static.backgroundLayer02},
            ${theme.kallisto.color.background.static.backgroundLayer02}
          );
        border: 1px solid ${color}20;
        display: flex;
        font-size: ${(p) => p.theme.kallisto.font.size['175']};
        pointer-events: auto;
      `}
    >
      {children}
    </Card>
  );
}
